import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Building, Download } from 'lucide-react';
import { usePermissions } from '@contexts/AuthContext';
import { supplierService, noteService } from '@services';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { useSupplierPageData } from '@hooks/useSupplierPageData';
import PageLayout from '@components/layout/PageLayout';
import Badge from '@components/common/Badge';
import TierBadge from '@components/common/TierBadge';
import Button from '@components/common/Button';
import ListPageHeader from '@components/common/ListPageHeader';
import { FilterButtons } from '@components/common/FilterButtons';
import { RZTable, ColumnSelector } from '@components/common/table';
import GenericModal from '@components/common/modals/GenericModal';
import { CreateSupplierForm } from '@components/features/suppliers/CreateSupplierForm';
import { Supplier, TableColumn, ApiError, CreateSupplierRequest } from '@app-types';
import { exportSuppliers } from '@utils/exportUtils';
import { showToast } from '@utils/toast';
import { supplierStatusToVariant, tierToVariant } from '@utils/badgeMappers';
import { useReferenceCatalog } from '@hooks/useReferenceCatalog';
import { getSupplierStatusLabel } from '@utils/referenceCatalog';
import { formatInstantAsDate } from '@utils/dateUtils';
import styles from './SuppliersPage.module.css';

// Column configuration for the suppliers table
const supplierColumns: TableColumn[] = [
  {
    key: 'name',
    label: 'Supplier Name',
    width: '300px',
    defaultVisible: true,
    alwaysVisible: true,
    filterKey: 'name',
    sortable: true
  },
  {
    key: 'vendor_id',
    label: 'Supplier Number',
    width: '180px',
    defaultVisible: true,
    filterKey: 'vendor_id',
    sortable: true
  },
  { key: 'tier', label: 'Tier', width: '150px', defaultVisible: true, filterKey: 'tier', sortable: true },
  { key: 'status', label: 'Status', width: '120px', defaultVisible: true, filterKey: 'status', sortable: true },
  {
    key: 'last_note',
    label: 'Last Note',
    width: '260px',
    defaultVisible: true,
    filterable: false,
    sortable: false
  },
  {
    key: 'created_date',
    label: 'Created Date',
    width: '150px',
    defaultVisible: false,
    filterable: false,
    sortable: true
  }
];

// Status filter options (all statuses including Deleted)
const allStatusFilters = [
  { key: 'all', label: 'All' },
  { key: 'Active', label: 'Active' },
  { key: 'Inactive', label: 'Inactive' },
  { key: 'Deleted', label: 'Deleted' }
];

const SuppliersPage: React.FC = () => {
  const permissions = usePermissions();
  useReferenceCatalog();

  // Filter status options based on user role (Deleted only for Admin)
  const labeledStatusFilters = allStatusFilters.map((filter) => ({
    ...filter,
    label: filter.key === 'all' ? filter.label : getSupplierStatusLabel(filter.key) || filter.label,
  }));
  const statusFilters = permissions.isAdmin
    ? labeledStatusFilters
    : labeledStatusFilters.filter((f) => f.key !== 'Deleted');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');

  // Pagination with localStorage persistence
  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'suppliers-page-size',
    defaultPageSize: 25
  });

  // Column filters with URL persistence and debounce
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns: supplierColumns });

  // Sorting with hook
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: {
      name: 's.name',
      vendor_id: 's.vendor_id',
      tier: 'st.name',
      status: 's.status',
      created_date: 's.created_at'
    },
    defaultSort: { field: 's.name', direction: 'ASC' },
    onPageReset: () => setCurrentPage(1)
  });

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('suppliers-visible-columns');
    if (stored) {
      return new Set(JSON.parse(stored));
    }
    // Default visible columns
    return new Set(supplierColumns.filter((col) => col.defaultVisible).map((col) => col.key));
  });

  // Save column preferences to localStorage
  useEffect(() => {
    localStorage.setItem('suppliers-visible-columns', JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Modal state for creating supplier
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Create supplier mutation
  const createMutation = useMutation({
    mutationFn: async ({ data, note }: { data: CreateSupplierRequest; note: string }) => {
      const supplier = await supplierService.createSupplier(data);
      const trimmed = note.trim();
      if (trimmed) {
        try {
          await noteService.createNote('supplier', supplier.id, { content: trimmed });
        } catch (err) {
          // Best-effort: a failed note save must NOT fail supplier creation (decision 3).
          console.error('Failed to save supplier note:', err);
        }
      }
      return supplier;
    },
    onSuccess: (supplier) => {
      refreshData();
      setShowCreateModal(false);
      showToast.success('Supplier created successfully');
      // Optional: navigate to detail page
      // navigate(`/suppliers/${supplier.id}`);
    },
    onError: (error: ApiError) => {
      console.error('Failed to create supplier:', error);
    }
  });

  // Toggle column visibility
  const toggleColumn = (columnKey: string) => {
    const column = supplierColumns.find((col) => col.key === columnKey);
    if (column?.alwaysVisible) return; // Don't allow hiding always-visible columns

    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Check if a column should be visible
  const isColumnVisible = (columnKey: string): boolean => {
    return visibleColumns.has(columnKey);
  };

  // Get visible columns for current user
  const getVisibleColumns = (): TableColumn[] => {
    return supplierColumns;
  };

  // Get columns that should actually be rendered (visible + available for user role)
  const getRenderedColumns = (): TableColumn[] => {
    return getVisibleColumns().filter((col) => isColumnVisible(col.key));
  };

  // Render cell content based on column type
  const renderCellContent = (column: TableColumn, supplier: Supplier) => {
    switch (column.key) {
      case 'name':
        // Make supplier name non-clickable for deleted suppliers
        if (supplier.status === 'Deleted') {
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9ca3af', cursor: 'not-allowed' }}>
              <Building size={16} style={{ color: '#9ca3af' }} />
              <span>{supplier.name}</span>
            </div>
          );
        }
        return (
          <button className={styles.tableLink} onClick={() => navigate(`/suppliers/${supplier.id}`)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Building size={16} style={{ color: '#6b7280' }} />
              <span>{supplier.name}</span>
            </div>
          </button>
        );

      case 'vendor_id':
        return supplier.vendor_id || 'N/A';

      case 'tier':
        return <TierBadge variant={tierToVariant(supplier.tier_name)}>{supplier.tier_name}</TierBadge>;

      case 'status':
        if (supplier.status === 'Deleted') {
          return <Badge variant="error">Deleted</Badge>;
        }
        return <Badge variant={supplierStatusToVariant(supplier.status)}>{getSupplierStatusLabel(supplier.status)}</Badge>;

      case 'last_note': {
        // AC-4: neutral placeholder when a supplier has no notes
        if (!supplier.last_note) {
          return <span style={{ color: '#9ca3af' }}>—</span>;
        }
        const truncatedNote = (
          <span
            style={{
              display: 'inline-block',
              maxWidth: '240px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              verticalAlign: 'bottom'
            }}
          >
            {supplier.last_note}
          </span>
        );
        // AC-3: quick access to the full Notes history (on the detail page) + full text on hover.
        // Deleted suppliers keep the note visible but non-clickable, mirroring the name cell.
        if (supplier.status === 'Deleted') {
          return (
            <span title={supplier.last_note} style={{ color: '#9ca3af', cursor: 'not-allowed' }}>
              {truncatedNote}
            </span>
          );
        }
        return (
          <button
            className={styles.tableLink}
            onClick={() => navigate(`/suppliers/${supplier.id}`)}
            title={supplier.last_note}
          >
            {truncatedNote}
          </button>
        );
      }

      case 'created_date':
        return formatInstantAsDate(supplier.created_at);

      default:
        return supplier[column.key as keyof Supplier] || '-';
    }
  };

  // Update URL when filters change
  // Note: Pagination (page, limit) is handled by usePagination hook
  useEffect(() => {
    const params = new URLSearchParams(searchParams); // Preserve existing params (page, limit)

    if (searchQuery) {
      params.set('search', searchQuery);
    } else {
      params.delete('search');
    }

    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    setSearchParams(params, { replace: true });
  }, [searchQuery, statusFilter, searchParams, setSearchParams]);

  // Reset to page 1 when filters change
  useEffect(() => {
    resetToFirstPage();
  }, [searchQuery, statusFilter, debouncedColumnFilters, resetToFirstPage]);

  // Fetch suppliers with filters using hook
  const {
    suppliers,
    isLoading,
    error,
    isFetching,
    filterCounts,
    totalItems: totalSuppliers,
    totalPages,
    refreshData
  } = useSupplierPageData({
    queryKey: ['suppliers', searchQuery, statusFilter, currentPage, pageSize, sortConfig, debouncedColumnFilters],
    pageSize, // Pass pageSize for correct totalPages calculation
    queryFn: () => {
      const filters = {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sortBy: sortConfig.field,
        order: sortConfig.direction
      };
      return supplierService.getSuppliers(filters);
    }
  });

  // Handle search
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  // Handle filter change
  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  };

  // Export mutation - fetches the full filtered dataset (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () =>
      supplierService.getSuppliersForExport({
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        columnFilters: getColumnFiltersParam() || undefined
      }),
    onSuccess: (response) => {
      exportSuppliers(response.suppliers, Array.from(visibleColumns));
    },
    onError: (err: ApiError) => {
      console.error('Failed to export suppliers:', err);
      showToast.error('Failed to export suppliers. Please try again.');
    }
  });

  // Handle export
  const handleExport = () => {
    exportMutation.mutate();
  };

  // Shared close handler for the Create Supplier modal (X button, Escape, and Cancel button all
  // funnel through here) so a stale createMutation.error can never survive a close/reopen cycle.
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    createMutation.reset();
  };

  if (error) {
    return (
      <PageLayout>
        <div className={styles.errorState}>
          <p>Error loading suppliers. Please try again.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <ListPageHeader
        icon={<Building size={24} />}
        title="Suppliers"
        count={filterCounts.all || 0}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search suppliers, tiers..."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={exportMutation.isPending || !suppliers || suppliers.length === 0}
            >
              <Download size={16} />
              {exportMutation.isPending ? 'Exporting...' : 'Export List'}
            </Button>

            <ColumnSelector
              columns={getVisibleColumns()}
              visibleColumns={Array.from(visibleColumns)}
              onToggleColumn={toggleColumn}
            />
          </>
        }
        additionalContent={
          permissions.canCreateSuppliers ? (
            <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
              <Plus size={16} />
              New Supplier
            </Button>
          ) : undefined
        }
      />

      {/* Professional Filters */}
      <FilterButtons
        filters={statusFilters}
        activeFilter={statusFilter}
        onFilterChange={handleFilterChange}
        filterCounts={filterCounts}
        dataTestId="suppliers-filter"
      />

      {/* Loading overlay when fetching data */}
      {isFetching && !isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {/* Professional Table */}
      <div className={styles.suppliersTable}>
        <RZTable
          data={suppliers}
          columns={getRenderedColumns()}
          getRowKey={(supplier) => supplier.id}
          renderCell={renderCellContent}
          sorting={{
            sortConfig,
            onSort: handleSort,
            isSorted
          }}
          columnFilters={{
            values: columnFilterValues,
            onChange: setColumnFilter,
            onClearAll: clearColumnFilters,
            hasActiveFilters: hasActiveColumnFilters
          }}
          pagination={{
            currentPage,
            totalPages,
            totalItems: totalSuppliers,
            pageSize,
            onPageChange: setCurrentPage,
            onPageSizeChange: setPageSize,
            itemName: 'suppliers',
            pageSizeOptions: [25, 50, 100]
          }}
          state={{
            isLoading,
            isEmpty: suppliers.length === 0,
            loadingMessage: 'Loading suppliers...'
          }}
          emptyState={
            <RZTable.Empty
              icon={<Building size={48} />}
              title="No suppliers found"
              description="Try changing filters or search criteria"
            >
              {permissions.canCreateSuppliers && (
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  <Plus size={16} />
                  Create Your First Supplier
                </Button>
              )}
            </RZTable.Empty>
          }
        />
      </div>

      {/* Create Supplier Modal */}
      <GenericModal
        isOpen={showCreateModal}
        onClose={handleCloseCreateModal}
        title="Create New Supplier"
        size="md"
        disableBackdropClick
      >
        <CreateSupplierForm
          onSubmit={(data, note) => createMutation.mutate({ data, note })}
          onCancel={handleCloseCreateModal}
          isSubmitting={createMutation.isPending}
          error={createMutation.error}
        />
      </GenericModal>
    </PageLayout>
  );
};

export default SuppliersPage;
